/**
 * Loads App Store localized prices from RevenueCat offerings (iOS native IAP only).
 */

import { useQuery } from '@tanstack/react-query';
import { membershipCheckoutUsesIap } from '@/services/membershipPaymentService';
import {
  fetchIosPackagePriceMap,
  isRevenueCatSdkAvailable,
  type IosPackagePriceMap,
} from '@/services/revenueCatService';
import { useAuthStore } from '@/stores/authStore';
import { resolveRevenueCatAppUserId } from '@/utils/revenueCatAppUserId';

export const IOS_SUBSCRIPTION_PRICES_KEY = 'ios-subscription-prices';

export function useIosSubscriptionPrices(appUserId: string | undefined) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const iapEnabled = membershipCheckoutUsesIap();
  const enabled =
    isRevenueCatSdkAvailable() && iapEnabled && isAuthenticated;

  return useQuery<IosPackagePriceMap, Error>({
    queryKey: [IOS_SUBSCRIPTION_PRICES_KEY, appUserId],
    queryFn: async () => {
      const id = appUserId ?? (await resolveRevenueCatAppUserId());
      if (!id) {
        throw new Error('Could not resolve account for App Store prices');
      }
      return fetchIosPackagePriceMap(id);
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}
